/**
 * Status-dot rules — pure logic. A rule's guest condition is the standard
 * ConditionGroup tree; matching is done client-side with the parity-port
 * evaluator, so a dot means exactly what the same condition means everywhere
 * else (guest filters, workflows, display conditions). Rules evaluate
 * GUEST-level data (`checkedIn` = event check-in, not the per-appointment
 * `checkInAt`), so one guest shows identical dots on every surface.
 */
import { evaluateConditionGroup } from "@/lib/utils/evaluate-condition";
import {
  conditionReferencedKeys,
  hasNonEmptyConditions,
} from "@/components/ConditionBuilder/condition-utils";
import type { Guest } from "@/types/Guest";
import type { StatusDotRule } from "./types";

/** Rules that can actually match: non-empty condition. A condition-less rule
 *  would match every guest (evaluator's fire-on-no-conditions semantics). */
export function activeStatusDotRules(
  rules: StatusDotRule[] | undefined,
): StatusDotRule[] {
  if (!rules || rules.length === 0) return [];
  return rules.filter((r) => hasNonEmptyConditions(r.condition));
}

/**
 * Projection field list for the slotting guests/by-ids fetch: name fields ∪
 * every key the rules reference, sent RAW (dotted `additionalFields.X` stays
 * dotted). The BE sanitizer allowlists exactly these condition-key shapes and
 * itself collapses dotted keys to the parent `additionalFields` projection —
 * collapsing FE-side would send a bare "additionalFields", which the
 * allowlist rejects (it is not a valid condition key), silently dropping the
 * projection and blinding every custom-field rule. Stable order, deduped.
 */
export function statusDotFetchFields(rules: StatusDotRule[]): string[] {
  const fields = new Set<string>(["guestId", "firstName", "lastName", "email"]);
  for (const rule of rules) {
    for (const key of conditionReferencedKeys(rule.condition)) {
      fields.add(key);
    }
  }
  return [...fields];
}

/** Ordered list of rules matching one guest. */
export function matchStatusDots(
  rules: StatusDotRule[],
  guest: Guest | undefined,
): StatusDotRule[] {
  if (!guest || rules.length === 0) return [];
  return rules.filter(
    (r) =>
      hasNonEmptyConditions(r.condition) &&
      evaluateConditionGroup(r.condition, guest as unknown as Record<string, unknown>),
  );
}

/** Union across guests (multi-guest tiles): rule order preserved, one dot per
 *  rule even when several guests match it. */
export function unionStatusDots(
  rules: StatusDotRule[],
  guests: Array<Guest | undefined>,
): StatusDotRule[] {
  if (rules.length === 0 || guests.length === 0) return [];
  return rules.filter(
    (r) =>
      hasNonEmptyConditions(r.condition) &&
      guests.some(
        (g) =>
          g &&
          evaluateConditionGroup(r.condition, g as unknown as Record<string, unknown>),
      ),
  );
}

/** Signature of the rules' referenced keys — when it changes, previously
 *  fetched guest records may lack a newly needed projection field and must be
 *  refetched. */
export function statusDotFieldsSignature(rules: StatusDotRule[]): string {
  return statusDotFetchFields(rules).join(",");
}
